/**
 * Centralize endpoints used to determine access for easier sync between webapp and tests
 */
export interface ApiProp {
  endpoint: string
  method: string
}

/** Helper to make object for each endpoint */
function make(endpoint: string, method: string): ApiProp {
  return { endpoint, method }
}

/**
 * Conventions to maintain:
 * - alphabetical order
 * - name the variable as a combination of endpoint and method
 */
export const apiKeysAllForCompanyGet = make('/apikeys/all-for-company', 'get')
export const apiKeysGet = make('/apikeys', 'get')
export const apiKeysPost = make('/apikeys', 'post')
export const attacksActiveGet = make('/attacks/active', 'get')
export const attacksActivePagedGet = make('/attacks/active/paged', 'get')
export const attacksCompletedPagedGet = make('/attacks/completed/paged', 'get')
export const attacksIdHaltPost = make('/attacks/{guid}/halt', 'post')

export const clientsIdActivatePut = make(
  '/clients/{identifier}/activate',
  'put'
)
export const clientsIdDelete = make('/clients/{identifier}', 'delete')
export const companiesIdAuthPrefsPost = make(
  '/companies/{identifier}/auth/prefs',
  'post'
)
export const companiesIdClientsGet = make(
  '/companies/{identifier}/clients',
  'get'
)
export const companiesIdGet = make('/companies/{identifier}', 'get')
export const companiesIdPatch = make('/companies/{identifier}', 'patch')
export const companiesIdInvitesIdDelete = make(
  '/companies/{identifier}/invites/{email}',
  'delete'
)
export const companiesIdInvitesPost = make(
  '/companies/{identifier}/invites',
  'post'
)
export const companiesIdSamlClaimAliasesGet = make(
  '/companies/{identifier}/saml/claim-aliases',
  'get'
)
export const companiesIdSamlClaimAliasesPost = make(
  '/companies/{identifier}/saml/claim-aliases',
  'post'
)
export const companiesIdSamlClaimAliasDelete = make(
  '/companies/{identifier}/saml/claim-aliases/{aliasId}',
  'delete'
)
export const companiesIdSamlClaimAliasGet = make(
  '/companies/{identifier}/saml/claim-aliases/{aliasId}',
  'get'
)
export const companiesIdSamlClaimAliasPut = make(
  '/companies/{identifier}/saml/claim-aliases/{aliasId}',
  'put'
)
export const companiesIdSamlPropsPost = make(
  '/companies/{identifier}/saml/props',
  'post'
)
export const companiesIdUsersGet = make('/companies/{identifier}/users', 'get')
export const companiesIdUsersIdActiveDelete = make(
  '/companies/{identifier}/users/{email}/active',
  'delete'
)
export const companiesIdUsersIdActivePost = make(
  '/companies/{identifier}/users/{email}/active',
  'post'
)
export const companiesIdUsersIdPut = make(
  '/companies/{identifier}/users/{email}',
  'put'
)
export const companiesZonesGet = make('/companies/zones', 'get')
export const companiesZonesPost = make('/companies/zones', 'post')
export const companiesZonesZoneIdDelete = make(
  '/companies/zones/{zoneId}',
  'delete'
)
export const companiesZonesZoneIdPut = make('/companies/zones/{zoneId}', 'put')

export const externalIntegrationsDelete = make(
  '/external-integrations',
  'delete'
)
export const externalIntegrationsGet = make('/external-integrations', 'get')
export const externalIntegrationsLoadGeneratorGet = make(
  '/external-integrations/load-generator',
  'get'
)
export const externalIntegrationsLoadGeneratorPost = make(
  '/external-integrations/load-generator',
  'post'
)
export const externalIntegrationsLoadGeneratorPut = make(
  '/external-integrations/load-generator',
  'put'
)
export const externalIntegrationsStatusChecksGet = make(
  '/external-integrations/status-check',
  'get'
)
export const externalIntegrationsStatusChecksPost = make(
  '/external-integrations/status-check',
  'post'
)
export const externalIntegrationsStatusChecksPut = make(
  '/external-integrations/status-check',
  'put'
)

export const failureFlagsAppsProtectPost = make(
  '/failure-flags/apps/{name}/protect',
  'post'
)
export const failureFlagsAppsUnprotectPost = make(
  '/failure-flags/apps/{name}/unprotect',
  'post'
)
export const failureFlagsExperimentsGet = make(
  '/failure-flags/experiments',
  'get'
)
export const failureFlagsExperimentsActiveGet = make(
  '/failure-flags/experiments/active',
  'get'
)
export const failureFlagsExperimentsCompletedGet = make(
  '/failure-flags/experiments/completed/paged',
  'get'
)

export const integrationClientsIdActivatePut = make(
  '/integration-clients/{identifier}/activate',
  'put'
)
export const integrationClientsIdDelete = make(
  '/integration-clients/{identifier}',
  'delete'
)
export const integrationsGet = make('/integrations', 'get')
export const integrationsTypeDelete = make('/integrations/{type}', 'delete')
export const integrationsTypePut = make('/integrations/{type}', 'put')

export const largeScaleTestReportsPagedGet = make(
  '/large-scale-test-reports',
  'get'
)
export const largeScaleTestReportsIdGet = make(
  '/large-scale-test-reports/{largeScaleTestId}',
  'get'
)
export const largeScaleTestsActiveGet = make('/large-scale-tests/active', 'get')
export const largeScaleTestsDraftGet = make('/large-scale-tests/draft', 'get')
export const largeScaleTestsIdGet = make(
  '/large-scale-tests/{largeScaleTestId}',
  'get'
)
export const largeScaleTestsIdDelete = make(
  '/large-scale-tests/{largeScaleTestId}',
  'delete'
)
export const largeScaleTestsIdCompletePost = make(
  '/large-scale-tests/{largeScaleTestId}/complete',
  'post'
)
export const largeScaleTestsIdHaltAllPost = make(
  '/large-scale-tests/{largeScaleTestId}/halt-all',
  'post'
)
export const largeScaleTestsIdPrerequisitesPost = make(
  '/large-scale-tests/{largeScaleTestId}/prerequisites',
  'post'
)
export const largeScaleTestsIdRunAllPost = make(
  '/large-scale-tests/{largeScaleTestId}/run-all',
  'post'
)
export const largeScaleTestsIdServiceRunPost = make(
  '/large-scale-tests/{largeScaleTestId}/run',
  'post'
)
export const largeScaleTestsIdPut = make(
  '/large-scale-tests/{largeScaleTestId}',
  'put'
)
export const largeScaleTestsPost = make('/large-scale-tests', 'post')

export const notificationSettingsGet = make('/notification-settings', 'get')
export const notificationSettingsPatch = make('/notification-settings', 'patch')

export const orgsAuthCertificateDelete = make(
  '/orgs/auth/certificate',
  'delete'
)
export const orgsAuthCertificateGet = make('/orgs/auth/certificate', 'get')
export const orgsAuthCertificateOldDelete = make(
  '/orgs/auth/certificate/old',
  'delete'
)
export const orgsAuthCertificatePost = make('/orgs/auth/certificate', 'post')
export const orgsAuthSecretResetPost = make('/orgs/auth/secret/reset', 'post')
export const orgsIdGet = make('/orgs/{identifier}', 'get')
export const orgsPost = make('/orgs', 'post')

export const policiesServiceIdReliabilityReportGet = make(
  '/policies/{serviceId}/reliability-report',
  'get'
)

export const reliabilityTestsActiveGet = make(
  '/reliability-tests/active',
  'get'
)
export const reliabilityTestsActivePagedGet = make(
  '/reliability-tests/active/paged',
  'get'
)
export const reliabilityTestsCompletedPagedGet = make(
  '/reliability-tests/completed/paged',
  'get'
)
export const reliabilityTestsNextGet = make(
  '/reliability-tests/next-runs',
  'get'
)
export const reliabilityTestsRecentGet = make(
  '/reliability-tests/recent-runs',
  'get'
)

export const reportsCompaniesGet = make('/reports/companies', 'get')
export const reportsPricingGet = make('/reports/pricing', 'get')
export const reportsTeamsPagedGet = make('/reports/teams/paged', 'get')
export const rolesIdPatch = make('/roles/{roleId}', 'patch')
export const rolesPost = make('/roles', 'post')

export const scenariosActiveGet = make('/scenarios/active', 'get')
export const scenariosActivePagedGet = make('/scenarios/active/paged', 'get')
export const scenariosActiveRunsGet = make('/scenarios/active-runs', 'get')
export const scenariosCompletedPagedGet = make(
  '/scenarios/completed/paged',
  'get'
)
export const scenariosDraftsGet = make('/scenarios/drafts', 'get')
export const scenariosGet = make('/scenarios', 'get')
export const scenariosHaltIdRunsRunNumberPost = make(
  '/scenarios/halt/{guid}/runs/{runNumber}',
  'post'
)
export const scenariosIdRunsGet = make('/scenarios/{guid}/runs', 'get')
export const scenariosIdRunsPost = make('/scenarios/{guid}/runs', 'post')
export const scenariosRunsGet = make('/scenarios/runs', 'get')
export const scenariosSharedIdDelete = make(
  '/scenarios/shared/{guid}',
  'delete'
)
export const scenariosSharedPost = make('/scenarios/shared', 'post')
export const schedulesAttacksPagedGet = make('/schedules/attacks/paged', 'get')
export const schedulesScenariosPagedGet = make(
  '/schedules/scenarios/paged',
  'get'
)
export const schedulesServicesPagedGet = make(
  '/schedules/services/paged',
  'get'
)
export const servicesBulkCreateWithLoadBalancersPost = make(
  '/services/bulk-create-with-load-balancers',
  'post'
)
export const servicesGet = make('/services', 'get')
export const sharedAssetsRequestsPost = make('/sharedAssets/requests', 'post')
export const sharedAssetsRequestsApprovePost = make(
  '/sharedAssets/requests/approve',
  'post'
)
export const sharedAssetsRequestsDeletePost = make(
  '/sharedAssets/requests/delete',
  'post'
)
export const sharedAssetsRequestsDenyPost = make(
  '/sharedAssets/requests/deny',
  'post'
)

export const teamsIdDelete = make('/teams/{identifier}', 'delete')
export const teamsIdPatch = make('/teams/{identifier}', 'patch')
export const teamsClientInstallGet = make('/teams/client/install', 'get')
export const teamsKubernetesInstallGet = make(
  '/teams/kubernetes/install',
  'get'
)
export const teamsHelmInstallGet = make('/teams/helm/install', 'get')
export const teamsPagedGet = make('/teams/paged', 'get')
export const testSuitesGet = make('/test-suites', 'get')
export const testSuitesIdDelete = make('/test-suites/{testSuiteId}', 'delete')
export const testSuitesIdPatch = make('/test-suites/{testSuiteId}', 'patch')
export const testSuitesPost = make('/test-suites', 'post')

export const usersAuthMfaForceDisablePost = make(
  '/users/auth/mfa/forceDisable',
  'post'
)
export const usersAuthMfaInfoGet = make('/users/auth/mfa/info', 'get')
export const usersAuthPasswordPut = make('/users/auth/password', 'put')
export const usersGet = make('/users', 'get')
export const usersIdDelete = make('/users/{email}', 'delete')
export const usersInviteIdDelete = make('/users/invite/{email}', 'delete')
export const usersIdPut = make('/users/{email}', 'put')
export const usersPost = make('/users', 'post')
export const usersSelfPatch = make('/users/self', 'patch')

export const webhooksGet = make('/webhooks', 'get')
export const webhooksPost = make('/webhooks', 'post')
export const webhooksIdPut = make('/webhooks/{guid}', 'put')
